import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Send, FileText, ShieldCheck, LogOut, Menu, Bot, MessagesSquare, ListFilter, Zap, Plug, BarChart3, HelpCircle, Radio, Layers, KeyRound, Mail, MessageSquare, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { useWorkspaces } from "@/lib/useWorkspaces";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { SESSION_EXPIRED_EVENT } from "@/components/SessionExpiredDialog";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/rubrica", label: "Rubrica & Cluster", icon: Users, id: "contacts" },
  { to: "/liste", label: "Liste", icon: ListFilter, id: "lists" },
  { to: "/form", label: "FORM", icon: ClipboardList, id: "forms", forms: true },
  { to: "/campagne", label: "Campagne", icon: Send, id: "campaigns" },
  { to: "/sms", label: "SMS", icon: MessageSquare, id: "sms" },
  { to: "/otp", label: "OTP – Registro", icon: KeyRound, id: "otp", otp: true },
  { to: "/template", label: "Template", icon: FileText, id: "templates" },
  { to: "/consensi", label: "Consensi (GDPR)", icon: ShieldCheck, id: "consent" },
  { to: "/assistente", label: "Assistente IA", icon: Bot, id: "ai" },
  { to: "/automazioni", label: "Automazioni", icon: Zap, id: "automations" },
  { to: "/chat", label: "Chat", icon: MessagesSquare, id: "chat" },
  { to: "/email", label: "Email transazionali", icon: Mail, id: "email" },
  { to: "/integrazioni", label: "Integrazioni", icon: Plug, id: "integrations" },
  { to: "/report", label: "Report & Costi", icon: BarChart3, id: "report" },
  { to: "/canali", label: "Canali", icon: Radio, id: "channels", admin: true },
  { to: "/account-meta", label: "Account Meta", icon: Radio, id: "account-meta" },
  { to: "/fatturazione", label: "Consumi & Fatturazione", icon: BarChart3, id: "billing" },
  { to: "/superadmin", label: "Super Admin", icon: Layers, id: "superadmin", admin: true },
  { to: "/panoramica-canali", label: "Panoramica canali", icon: Layers, id: "channels-overview", admin: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [open, setOpen] = useState(false);
  const [hotClicks, setHotClicks] = useState(0);
  const [otpVisible, setOtpVisible] = useState(false);
  const [formsVisible, setFormsVisible] = useState(false);
  const { active: activeWs } = useWorkspaces();
  const [pwOpen, setPwOpen] = useState(false);
  const forcedPw = !!user?.must_change_password;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    api.get("/dashboard/quota").then((r) => setQuota(r.data.quota)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    api.get("/otp/registry/me").then((r) => setOtpVisible(!!r.data.visible)).catch(() => setOtpVisible(false));
    api.get("/forms/config").then((r) => setFormsVisible(!!r.data.enabled)).catch(() => setFormsVisible(false));
  }, [activeWs?.id]);

  useEffect(() => {
    const poll = () => api.get("/chat/hot-clicks").then((r) => setHotClicks(r.data.count || 0)).catch(() => {});
    poll();
    const id = setInterval(poll, 30000);
    const stop = () => clearInterval(id);
    window.addEventListener(SESSION_EXPIRED_EVENT, stop);
    return () => { clearInterval(id); window.removeEventListener(SESSION_EXPIRED_EVENT, stop); };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0E17] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-[100dvh] w-72 bg-[#0B1120] border-r border-[#1F2937] flex flex-col overflow-y-auto overscroll-contain transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="px-6 py-6 flex items-center gap-3 border-b border-[#1F2937]">
          <img src="/logo.png" alt="Nouvelle WhatsApp" className="h-14 w-14 rounded-2xl bg-[#0F172A] p-1.5" data-testid="brand-logo" />
          <div>
            <p className="font-display font-extrabold text-slate-100 leading-tight">Nouvelle <span className="text-[#10B981]">WhatsApp</span></p>
            <p className="text-xs text-slate-500 flex items-center gap-1.5 truncate" data-testid="brand-channel-name">
              {activeWs ? <><span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: activeWs.color }} /><span className="truncate">{activeWs.name}</span></> : "Gestione servizio WhatsApp API"}
            </p>
          </div>
        </div>

        <WorkspaceSwitcher />

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.filter((item) => (!item.admin || user?.role === "admin") && (!item.otp || otpVisible) && (!item.forms || formsVisible)).map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.id}-link`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-[#10B981] text-[#0A0E17] shadow-lg shadow-[#10B981]/20"
                    : "text-slate-400 hover:text-slate-100 hover:bg-[#1F2937]/70"
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
                {item.label}
                {item.id === "chat" && hotClicks > 0 && (
                  <span data-testid="nav-chat-hot-clicks" title={`${hotClicks} contatti hanno cliccato un link con finestra 24h aperta`}
                        className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${active ? "bg-[#0A0E17] text-[#F59E0B]" : "bg-[#F59E0B] text-[#0A0E17]"}`}>
                    {hotClicks}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {quota && (
          <div className="mx-4 mb-4 p-4 rounded-xl bg-[#111827] border border-[#1F2937]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">Quota mese</span>
              <span className="text-xs font-mono2 text-[#10B981]">{quota.percent}%</span>
            </div>
            <Progress value={quota.percent} className="h-2 bg-[#1F2937]" />
            <p className="mt-2 text-sm font-mono2 text-slate-300" data-testid="sidebar-quota">
              {quota.used.toLocaleString("it-IT")} / {quota.limit.toLocaleString("it-IT")}
            </p>
            <p className="text-xs text-slate-500">contatti disponibili: {quota.remaining.toLocaleString("it-IT")}</p>
          </div>
        )}

        <div className="px-3 pb-5 space-y-2">
          <Link to="/manuale" data-testid="manual-link"
             className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-colors ${location.pathname === "/manuale" ? "text-[#10B981] bg-[#111827]" : "text-slate-400 hover:text-[#10B981] hover:bg-[#111827]"}`}>
            <FileText className="h-4 w-4" /> Manuale (web + PDF)
          </Link>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#111827] border border-[#1F2937]">
            <div className="h-9 w-9 rounded-full bg-[#1E293B] flex items-center justify-center text-sm font-semibold text-[#10B981]">
              {(user?.name || "A").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-200 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button onClick={() => setPwOpen(true)} data-testid="change-password-button" className="text-slate-500 hover:text-[#10B981] transition-colors" title="Cambia password">
              <KeyRound className="h-5 w-5" />
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-500 hover:text-[#EF4444] transition-colors"
              title="Esci"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>
      <ChangePasswordDialog open={pwOpen || forcedPw} forced={forcedPw} onClose={() => setPwOpen(false)} />

      {open && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-[#0B1120] border-b border-[#1F2937]">
          <button onClick={() => setOpen(true)} data-testid="menu-toggle" className="text-slate-300">
            <Menu className="h-6 w-6" />
          </button>
          <img src="/logo.png" alt="Nouvelle WhatsApp" className="h-8 w-8 rounded-lg" />
          <span className="font-display font-bold text-slate-100 flex-1">Nouvelle <span className="text-[#10B981]">WhatsApp</span></span>
          <HelpButton pathname={location.pathname} compact />
        </header>
        <div className="hidden lg:block fixed top-4 right-4 z-30"><HelpButton pathname={location.pathname} /></div>
        <main className="px-4 sm:px-8 py-6 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}

const HELP_MAP = {
  "/": ["cap-2", "Dashboard"], "/rubrica": ["cap-3", "Rubrica"], "/liste": ["cap-3", "Liste dinamiche"], "/campagne": ["cap-5", "Campagne"],
  "/template": ["cap-4", "Template Meta"], "/consensi": ["cap-3", "Consenso e iscrizione"], "/assistente": ["cap-8", "Agente IA"],
  "/chat": ["cap-6", "Chat e finestra 24 ore"], "/automazioni": ["cap-7", "Automazioni"], "/integrazioni": ["cap-11", "Configurazione Meta"],
  "/report": ["cap-9", "Report & Costi"],
};

function HelpButton({ pathname, compact }) {
  const [cap, label] = HELP_MAP[pathname] || ["cap-1", "Panoramica"];
  if (pathname === "/manuale") return null;
  return (
    <Link to={`/manuale#${cap}`} data-testid="context-help-button" title={`Manuale: ${label}`}
          className={`group inline-flex items-center gap-2 rounded-full border border-[#1F2937] bg-[#111827]/90 backdrop-blur text-slate-300 hover:text-[#10B981] hover:border-[#10B981]/60 transition-colors ${compact ? "h-9 w-9 justify-center" : "h-10 px-3 shadow-lg shadow-black/30"}`}>
      <HelpCircle className="h-5 w-5" />
      {!compact && <span className="text-xs max-w-0 overflow-hidden group-hover:max-w-[200px] transition-all duration-300 whitespace-nowrap">Guida: {label}</span>}
    </Link>
  );
}
